import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, Merge, Search } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { catalogApi } from '../services/catalogApi';
import type { CatalogEntity, EntityMergeResult } from '../types/catalog';
import { colors, spacing, typography, borderRadius } from '../styles/theme';
import { displayEntityType } from '../utils/entityTerminology';

/**
 * Merge two or more entities into one.
 *
 * Nothing is destroyed without a preview first: the server reports what the
 * merge would do by running it and rolling back, so the numbers shown here are
 * the numbers that will happen.
 */

const Panel = styled.section`
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[4]};
  margin-bottom: ${spacing[4]};
`;

const Title = styled.h2`
  margin: 0 0 ${spacing[1]};
  font-size: ${typography.fontSizes.lg};
`;

const Muted = styled.p`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  margin: 0 0 ${spacing[3]};
`;

const SearchRow = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[3]};
`;

const Input = styled.input`
  flex: 1;
  padding: ${spacing[2]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSizes.sm};
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  border-radius: ${borderRadius.base};
  border: 1px solid ${props =>
    props.$variant === 'danger' ? colors.error[600] :
      props.$variant === 'primary' ? colors.primary[600] : colors.border.normal};
  background: ${props =>
    props.$variant === 'danger' ? colors.error[600] :
      props.$variant === 'primary' ? colors.primary[600] : colors.white};
  color: ${props => (props.$variant ? colors.white : colors.text.primary)};
  padding: ${spacing[2]} ${spacing[3]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${typography.fontSizes.sm};

  th, td {
    text-align: left;
    padding: ${spacing[2]};
    border-bottom: 1px solid ${colors.border.light};
  }
`;

const Warning = styled.div`
  display: flex;
  gap: ${spacing[2]};
  border: 1px solid ${colors.warning[300]};
  background: ${colors.warning[50]};
  border-radius: ${borderRadius.base};
  padding: ${spacing[3]};
  margin: ${spacing[3]} 0;
  font-size: ${typography.fontSizes.sm};
`;

const ErrorText = styled.div`
  color: ${colors.error[600]};
  font-size: ${typography.fontSizes.sm};
  margin-top: ${spacing[2]};
`;

/** Only the counters worth a sentence; the rest are noise when zero. */
const CHANGE_LABELS: Array<[keyof EntityMergeResult['changes'], string]> = [
  ['movedEventSources', 'Event sources moved'],
  ['duplicateEventSources', 'Duplicate event source links removed'],
  ['reactivatedEventSources', 'Event source links reactivated'],
  ['movedAliases', 'Aliases moved'],
  ['duplicateAliases', 'Duplicate aliases removed'],
  ['namesKeptAsAliases', 'Merged names kept as aliases'],
  ['slugsKeptAsAliases', 'URLs kept working as aliases'],
  ['movedMediaAssets', 'Logo assets moved'],
  ['demotedMediaAssets', 'Duplicate current logos demoted'],
  ['movedEventOverrides', 'Event overrides moved'],
  ['duplicateEventOverrides', 'Duplicate event overrides removed'],
  ['movedEventRelationships', 'Event relationships moved'],
  ['duplicateEventRelationships', 'Duplicate event relationships removed'],
  ['movedRelationshipEvidence', 'Relationship evidence moved'],
  ['movedAssessments', 'Source assessments moved'],
  ['movedDiscoveryQueries', 'Discovery queries moved'],
  ['movedDiscoveryRuns', 'Discovery runs moved'],
  ['duplicateDiscoveryRuns', 'Duplicate discovery runs removed'],
  ['updatedDiscoveryBatches', 'Discovery batches updated'],
  ['deletedEntities', 'Entities removed'],
];

export function AdminMergeEntitiesPage() {
  const [search, setSearch] = useState('');
  const [entities, setEntities] = useState<CatalogEntity[]>([]);
  const [survivorId, setSurvivorId] = useState<number | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [preview, setPreview] = useState<EntityMergeResult | null>(null);
  const [done, setDone] = useState<EntityMergeResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (term: string) => {
    try {
      setEntities(await catalogApi.listEntities(term, 50));
    } catch (loadError) {
      setError((loadError as Error).message);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  // A changed selection invalidates the preview it was based on.
  useEffect(() => { setPreview(null); }, [survivorId, selected]);

  const mergedIds = useMemo(
    () => selected.filter(id => id !== survivorId),
    [selected, survivorId]
  );

  const runPreview = async () => {
    if (!survivorId || mergedIds.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      setPreview(await catalogApi.mergeEntities(survivorId, mergedIds));
    } catch (previewError) {
      setError((previewError as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const runMerge = async () => {
    if (!survivorId || mergedIds.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const result = await catalogApi.mergeEntities(survivorId, mergedIds, { confirm: true });
      setDone(result);
      setPreview(null);
      setSelected([]);
      setSurvivorId(null);
      await load(search);
    } catch (mergeError) {
      setError((mergeError as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (entityId: number) => setSelected(current =>
    current.includes(entityId)
      ? current.filter(id => id !== entityId)
      : [...current, entityId]);

  const changeRows = (result: EntityMergeResult) =>
    CHANGE_LABELS.filter(([key]) => Number(result.changes[key]) > 0);

  return (
    <AdminLayout
      currentPage="mergeEntities"
      pageTitle="Merge Entities"
      pageDescription="Combine duplicate entities into a single record"
    >
      <Panel>
        <Title>Merge entities</Title>
        <Muted>
          Everything belonging to the entities you merge — event sources, aliases, logos,
          discovery history — moves to the one you keep. Duplicates are removed and their
          URLs keep working.
        </Muted>

        <SearchRow>
          <Input
            aria-label="Search entities"
            placeholder="Search entities by name"
            value={search}
            onChange={event => setSearch(event.target.value)}
            onKeyDown={event => { if (event.key === 'Enter') load(search); }}
          />
          <Button type="button" onClick={() => load(search)}>
            <Search size={16} aria-hidden /> Search
          </Button>
        </SearchRow>

        <Table aria-label="Entities">
          <thead>
            <tr>
              <th>Merge</th>
              <th>Keep</th>
              <th>Entity</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {entities.map(entity => (
              <tr key={entity.entityId}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Merge ${entity.entityName}`}
                    checked={selected.includes(entity.entityId)}
                    onChange={() => toggle(entity.entityId)}
                  />
                </td>
                <td>
                  <input
                    type="radio"
                    name="survivor"
                    aria-label={`Keep ${entity.entityName}`}
                    checked={survivorId === entity.entityId}
                    onChange={() => setSurvivorId(entity.entityId)}
                  />
                </td>
                <td>{entity.entityName}</td>
                <td>{displayEntityType(entity.entityType)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {entities.length === 0 && <Muted>No entities matched.</Muted>}
      </Panel>

      {survivorId && mergedIds.length > 0 && (
        <Panel>
          <Title>Review</Title>
          <Muted>
            Keeping <strong>{entities.find(e => e.entityId === survivorId)?.entityName}</strong>,
            merging {mergedIds.length} other{mergedIds.length === 1 ? '' : 's'} into it.
          </Muted>

          <Button type="button" onClick={runPreview} disabled={busy}>
            {busy && !preview ? 'Checking…' : 'Preview the merge'}
          </Button>

          {preview && (
            <>
              <Table aria-label="Merge preview">
                <thead>
                  <tr><th>What changes</th><th>Rows</th></tr>
                </thead>
                <tbody>
                  {changeRows(preview).map(([key, label]) => (
                    <tr key={key}><td>{label}</td><td>{preview.changes[key]}</td></tr>
                  ))}
                </tbody>
              </Table>

              <Warning>
                <AlertTriangle size={18} aria-hidden />
                <span>
                  This cannot be undone. {preview.merged.map(entity => entity.entityName).join(', ')}
                  {preview.merged.length === 1 ? ' will be' : ' will be'} removed.
                </span>
              </Warning>

              <Button type="button" $variant="danger" onClick={runMerge} disabled={busy}>
                <Merge size={16} aria-hidden />
                {busy ? 'Merging…' : 'Merge and delete the others'}
              </Button>
            </>
          )}

          {error && <ErrorText role="alert">{error}</ErrorText>}
        </Panel>
      )}

      {done && (
        <Panel>
          <Title>Merged</Title>
          <Muted>
            {done.merged.map(entity => entity.entityName).join(', ')} merged into{' '}
            <strong>{done.survivor.entityName}</strong>.
          </Muted>
          <Table aria-label="Merge result">
            <thead>
              <tr><th>What changed</th><th>Rows</th></tr>
            </thead>
            <tbody>
              {changeRows(done).map(([key, label]) => (
                <tr key={key}><td>{label}</td><td>{done.changes[key]}</td></tr>
              ))}
            </tbody>
          </Table>
        </Panel>
      )}

      {error && !survivorId && <ErrorText role="alert">{error}</ErrorText>}
    </AdminLayout>
  );
}

export default AdminMergeEntitiesPage;
